import React, { useEffect, useState } from 'react';
import '../../styled/pages/dashboard.scss';
import { Header, Loader, Success, Failed } from '../../components';
import { customStyles } from '../../Utils/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { addUserApi } from '../../rtk-apis/users/addUser';
import { editUserApi } from '../../rtk-apis/users/editUser';
import { getClinicApi } from '../../rtk-apis/clinics/getClinic';
import { getClinicGroupsApi } from '../../rtk-apis/clinics/getClinicGroups';
import { clearState } from '../../store/slices/users/addUser';
import { clearState as clearEditState } from '../../store/slices/users/editUser';
import { userFormValidationSchema } from '../auth/helper/Schema';
import { getCommonData } from "../../rtk-apis/common/getCommonData"
import BackButton from '../../components/BackButton';
import BreadCrumbs from '../../components/Breadcrumbs';
import { addAdminApi } from '../../rtk-apis/clinics/addAdmin';
import { getPrefillUser } from '../../rtk-apis/users/prefillUser';
import { clearAdminState } from '../../store/slices/clinics/addAdmin';
import { customList } from 'country-codes-list';

type FormContentProps = {
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  values: any;
  Role: string;
  hospitalName: string;
  hospitalId: string;
  editMode: boolean;
  prefillUserStatus: string;
  prefillUserResult: any;
  prefillUserMsg: string;
  RolesList: string[];
  clinicStatus: string;
  clinicResult: any;
  userDetails: any;
  setFilteredRoles: (roles: string[]) => void;
  setIsPrefillFieldsDisabled: (val: boolean) => void;
  setIsRoleDisabled: (val: boolean) => void;
  setSelectedHospitalId: (id: string) => void;
  setPrefillClinicName: (name: string) => void;
  setPrefillHospitalIdState: (id: string) => void;
  prefillClinicName: string;
  prefillHospitalIdState: string;
  setClinicNames: (names: any[]) => void;
};

/**
 * Defined outside AddUpdateUser so React never remounts it on parent re-renders.
 * Previously defined inside the component — caused all useEffects to re-run on
 * every keystroke, resetting clinicName and other fields unexpectedly.
 */
const FormContent = ({
  setFieldValue,
  values,
  Role,
  hospitalName,
  hospitalId,
  editMode,
  prefillUserStatus,
  prefillUserResult,
  prefillUserMsg,
  RolesList,
  clinicStatus,
  clinicResult,
  userDetails,
  setFilteredRoles,
  setIsPrefillFieldsDisabled,
  setIsRoleDisabled,
  setSelectedHospitalId,
  setPrefillClinicName,
  setPrefillHospitalIdState,
  prefillClinicName,
  prefillHospitalIdState,
  setClinicNames,
}: FormContentProps) => {

  // Lock clinic to current hospital only when the LOGGED-IN user is a Sonographer.
  // Do NOT use values.role here — that would reset clinic every time the admin
  // changes the target user's role, making the clinic field uneditable.
  useEffect(() => {
    if (Role === 'Sonographer' && hospitalName) {
      setFieldValue('clinicName', hospitalName);
      setSelectedHospitalId(hospitalId);
    }
  }, [Role, hospitalName, hospitalId, setFieldValue]);

  useEffect(() => {
    if (values.role === 'Admin' && prefillClinicName) {
      setFieldValue('clinicName', prefillClinicName);
      setSelectedHospitalId(prefillHospitalIdState);
    }
  }, [values.role, prefillClinicName, prefillHospitalIdState, setFieldValue]);

  useEffect(() => {
    if (!editMode && prefillUserStatus === 'SUCCESS' && prefillUserResult) {
      if (prefillUserResult.data.role === 'Admin') {
        setFieldValue('name', prefillUserResult.data.adminDetails?.name || '');
        setFieldValue('countryCode', prefillUserResult.data.adminDetails?.countryCode || '+61');
        setFieldValue('contactNumber', prefillUserResult.data.adminDetails?.contactNo || '');
        setPrefillClinicName(prefillUserResult.data.name);
        setPrefillHospitalIdState(prefillUserResult.data._id);
      } else {
        setFieldValue('name', prefillUserResult.data.name || '');
        setFieldValue('countryCode', prefillUserResult.data.countryCode || '+61');
        setFieldValue('contactNumber', prefillUserResult.data.contactDetails || '');
      }
      let fetchedRole = prefillUserResult.data.role;
      let switchRole;
      if (fetchedRole === 'Sonographer') {
        switchRole = 'Admin';
      } else if (fetchedRole === 'Admin') {
        switchRole = 'Sonographer';
      } else {
        switchRole = null;
      }
      setFieldValue('role', fetchedRole);

      if (prefillUserResult.data.role === 'Sonographer' || prefillUserResult?.data?.role === 'Radiologist' || prefillUserResult?.data?.role === 'Cardiologist') {
        setFieldValue('clinicName', hospitalName);
        setSelectedHospitalId(hospitalId);
      }

      if (prefillUserResult.data.role === 'Admin') {
        setFieldValue('clinicName', prefillUserResult.data.name);
        setSelectedHospitalId(prefillUserResult.data._id);
      }

      if (
        prefillUserResult.data.role === 'Radiologist' ||
        prefillUserResult.data.role === 'Cardiologist'
      ) {
        setIsRoleDisabled(true);
      } else {
        setIsRoleDisabled(false);
      }

      let availableRoles = RolesList;
      if (fetchedRole === 'Sonographer' || fetchedRole === 'Admin') {
        availableRoles = [fetchedRole, switchRole];
      }
      setFilteredRoles(availableRoles);

      setIsPrefillFieldsDisabled(
        prefillUserResult?.data?.role === 'Sonographer' || prefillUserResult?.data?.role === 'Admin' || prefillUserResult?.data?.role === 'Cardiologist' || prefillUserResult?.data?.role === 'Radiologist'
      );
    } else if (!editMode && prefillUserStatus === 'FAILED') {
      if (Role === 'Sonographer') {
        setFieldValue('clinicName', hospitalName);
        setSelectedHospitalId(hospitalId);
      }
      setFieldValue('name', '');
      setFieldValue('role', '');
      setFieldValue('clinicName', '');
      setFieldValue('countryCode', '+61');
      setFieldValue('contactNumber', '');
      setIsPrefillFieldsDisabled(false);
      setFilteredRoles(RolesList);
    }
  }, [
    prefillUserStatus,
    prefillUserResult,
    prefillUserMsg,
    editMode,
    Role,
    hospitalName,
    hospitalId,
    setFieldValue
  ]);

  useEffect(() => {
    if (clinicStatus === 'SUCCESS' && clinicResult?.data) {
      setClinicNames(clinicResult.data);
      if (editMode && userDetails?.clinicName && Role !== 'Sonographer') {
        const normalizedUserClinic = userDetails.clinicName.trim().toLowerCase();
        const matchingClinic = clinicResult.data.find(
          (clinic) => clinic.name.trim().toLowerCase() === normalizedUserClinic
        );
        if (matchingClinic) {
          setFieldValue('clinicName', matchingClinic.name);
          setSelectedHospitalId(matchingClinic._id);
        }
      }
    }
  }, [clinicStatus, clinicResult, editMode, userDetails, setFieldValue]);

  return null;
};

const AddUpdateUser = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const {
    status: addAdminStatus,
    msg: addAdminMsg,
  } = useSelector((state: RootState) => state.addAdminResponse);
  const { result: prefillUserResult, msg: prefillUserMsg, status: prefillUserStatus, loading: prefillUserLoading } = useSelector((state: RootState) => state.getPrefillUserResponse);
  const { userDetails } = location?.state || {};
  const editMode = location?.pathname?.includes('updateuser');
  const hospitalName = sessionStorage?.getItem("hospitalName");
  const hospitalId = sessionStorage?.getItem("hospitalId");
  const Role = sessionStorage?.getItem("role");

  const [displayMessage, setDisplayMessage] = useState('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);
  const [RolesList, setRolesList] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [clinicNames, setClinicNames] = useState([]);
  const [selectedHospitalId, setSelectedHospitalId] = useState('');
  const [isPrefillFieldsDisabled, setIsPrefillFieldsDisabled] = useState(false);
  const [isRoleDisabled, setIsRoleDisabled] = useState(false);
  const [prefillClinicName, setPrefillClinicName] = useState('');
  const [prefillHospitalIdState, setPrefillHospitalIdState] = useState('');
  // A Group Admin is added to a clinic group instead of a clinic (Super Admin only).
  const canAddGroupAdmin = Role === 'SUPER_ADMIN';
  const [clinicGroups, setClinicGroups] = useState<{ _id: string; name: string }[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');

  const [countryCode, setCountryCode] = useState('+61');

  const countryCodes = customList('countryCode', '{countryCallingCode}');
  const sortedUniqueCountryCodes = Array.from(
    new Map(
      Object.entries(countryCodes)
        .sort((a, b) => Number(a[1]) - Number(b[1]))
        .map(([code, callingCode]) => [callingCode, code])
    )
  ).map(([callingCode, code]) => ({
    code,
    callingCode: callingCode.padStart(2, '0'),
  }));

  const handleCountryChange = e => {
    setCountryCode(e.target.value);
  };

  const { status, result, msg, loading } = useSelector((state: RootState) => state.addUserResponse);
  const {
    status: editStatus,
    result: editResult,
    msg: editMsg,
    loading: editLoading,
  } = useSelector((state: RootState) => state.editUserResponse);
  const {
    status: clinicStatus,
    result: clinicResult,
    msg: clinicMsg,
    loading: clinicLoading,
  } = useSelector((state: RootState) => state.getClinicResponse);

  const {
    result: commonData,
  } = useSelector((state: RootState) => state.getCommonDataSliceResponse);

  useEffect(() => {
    if (Role === "Admin" || Role === "Sonographer") {
      setSelectedHospitalId(hospitalId);
    }
  }, [Role, hospitalId]);

  useEffect(() => {
    if (editMode && userDetails?.hospitalId) {
      setSelectedHospitalId(userDetails.hospitalId);
    }
  }, [editMode, userDetails]);

  useEffect(() => {
    const roleList: string[] = (commonData as any)?.roleList || [];
    if (roleList.length > 0) {
      const roles = canAddGroupAdmin ? roleList : roleList.filter(r => r !== 'Group Admin');
      setRolesList(roles);
      setFilteredRoles(roles);
    }
  }, [commonData, canAddGroupAdmin]);

  useEffect(() => {
    if (!canAddGroupAdmin) {
      return;
    }
    dispatch(getClinicGroupsApi())
      .unwrap()
      .then((res: any) => setClinicGroups(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setClinicGroups([]));
  }, [dispatch, canAddGroupAdmin]);

  useEffect(() => {
    const payload = {
      page: 1,
      limit: 100,
      search: '',
    };
    dispatch(getClinicApi(payload));
  }, []);

  const contactDetailsWithoutCountryCode = userDetails?.contactDetails?.replace(userDetails?.countryCode, '').trim() ?? '';

  const userFormInitialValues = {
    hospitalId: userDetails?.hospitalId ?? '',
    name: userDetails?.name ?? '',
    role: userDetails?.role ?? '',
    email: userDetails?.email ?? '',
    clinicName: Role === "Admin" || Role === "Sonographer" ? hospitalName : userDetails?.clinicName ? userDetails?.clinicName : '',
    countryCode: userDetails?.countryCode ?? '+61',
    contactNumber: contactDetailsWithoutCountryCode,
  };

  const handleSubmit = (values, { setSubmitting }) => {
    if (values.role === "Admin" && !editMode) {
      const adminPayload = {
        hospitalId: selectedHospitalId,
        name: values.name,
        email: values.email.toLowerCase(),
        contactNo: values.contactNumber,
        countryCode: values.countryCode,
      };
      dispatch(addAdminApi(adminPayload));
    } else if (editMode) {
      const payload = {
        id: userDetails?._id,
        hospitalId: userDetails.hospitalId,
        name: values.name,
        role: values.role,
        email: values.email.toLowerCase(),
        clinicName: values.clinicName,
        countryCode: values.countryCode,
        contactDetails: values.contactNumber,
      };
      dispatch(editUserApi(payload));
    } else if (values.role === 'Group Admin') {
      dispatch(
        addUserApi({
          clinicGroupId: selectedGroupId,
          clinicName: values.clinicName,
          name: values.name,
          role: values.role,
          email: values.email.toLowerCase(),
          countryCode: values.countryCode,
          contactDetails: values.contactNumber,
        })
      );
    } else {
      const payload = {
        hospitalId: Role === 'Sonographer' ? hospitalId : selectedHospitalId,
        name: values.name,
        role: values.role,
        email: values.email.toLowerCase(),
        clinicName: values.clinicName,
        countryCode: values.countryCode,
        contactDetails: values.contactNumber,
      };
      dispatch(addUserApi(payload));
    }
    setSubmitting(false);
  };

  useEffect(() => {
    const handleSuccess = message => {
      setDisplayMessage(message);
      setIsSuccessModalOpen(true);
      setTimeout(() => {
        setIsSuccessModalOpen(false);
        setDisplayMessage('');
        navigate('/users');
      }, 1500);
    };

    const handleFailure = message => {
      setDisplayMessage(message);
      setIsFailedModalOpen(true);
      setTimeout(() => {
        setIsFailedModalOpen(false);
        setDisplayMessage('');
      }, 1500);
    };
    if (status === 'SUCCESS' || editStatus === 'SUCCESS' || addAdminStatus === 'SUCCESS') {
      handleSuccess(status === 'SUCCESS' ? msg : editStatus === 'SUCCESS' ? editMsg : addAdminMsg);
    }

    if (status === 'FAILED' || editStatus === 'FAILED' || addAdminStatus === 'FAILED') {
      handleFailure(status === 'FAILED' ? msg : editStatus === 'FAILED' ? editMsg : addAdminMsg);
    }

    return () => {
      dispatch(clearState());
      dispatch(clearEditState());
      dispatch(clearAdminState());
    };
  }, [status, editStatus, addAdminStatus, msg, editMsg, addAdminMsg, navigate, dispatch]);

  const handleEmailChange = (e, setFieldValue) => {
    const emailValue = e.target.value;
    setFieldValue('email', emailValue);
    if (emailValue.includes('@') && !editMode) {
      dispatch(getPrefillUser({ email: emailValue.toLowerCase() }));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'BUTTON') {
      e.preventDefault();
    }
  };

  const breadcrumbs = [
    { label: 'Manage Users', link: '/users', icon: 'sidebar-icon' },
    { label: `${editMode ? 'Edit' : 'Add'} User` },
  ];

  if (editMode && !userDetails?.email) return null;

  return (
    <section
      className="layoutContainer"
      style={customStyles.layoutBackground}
    >
      <div className="layoutGrid">
        <div className="page">
          <Header />
          <div className="page__body">
            <Formik
              initialValues={userFormInitialValues}
              validationSchema={userFormValidationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ isSubmitting, setFieldValue, values }) => {
                return (
                  <>
                  <FormContent
                    setFieldValue={setFieldValue}
                    values={values}
                    Role={Role}
                    hospitalName={hospitalName}
                    hospitalId={hospitalId}
                    editMode={editMode}
                    prefillUserStatus={prefillUserStatus}
                    prefillUserResult={prefillUserResult}
                    prefillUserMsg={prefillUserMsg}
                    RolesList={RolesList}
                    clinicStatus={clinicStatus}
                    clinicResult={clinicResult}
                    userDetails={userDetails}
                    setFilteredRoles={setFilteredRoles}
                    setIsPrefillFieldsDisabled={setIsPrefillFieldsDisabled}
                    setIsRoleDisabled={setIsRoleDisabled}
                    setSelectedHospitalId={setSelectedHospitalId}
                    setPrefillClinicName={setPrefillClinicName}
                    setPrefillHospitalIdState={setPrefillHospitalIdState}
                    prefillClinicName={prefillClinicName}
                    prefillHospitalIdState={prefillHospitalIdState}
                    setClinicNames={setClinicNames}
                  />
                  <Form className="form" onKeyDown={handleKeyDown}>
                    <div className="page__body__head">
                      <BackButton />
                      <BreadCrumbs breadcrumbs={breadcrumbs} />
                      <div className="btn__group">
                        <button
                          className="btn btn__white btn__outlined"
                          type="button"
                          onClick={() => navigate('/users')}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn__primary"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {editMode ? 'Update' : 'Save'}
                        </button>
                      </div>
                    </div>
                    <section className="form__section">
                      <h1 className="form__section__title">User Details</h1>

                      <div className="formRow formRow__2">
                        <div className="formCol">
                          <div className="form__group">
                            <label htmlFor="email" className="form__label">
                              Email Address
                              <sup className="form__label__asterisk">*</sup>
                            </label>
                            <Field
                              type="text"
                              id="email"
                              name="email"
                              className="form__input form__input--sm"
                              placeholder="Email Address"
                              disabled={editMode}
                              onChange={(e) => handleEmailChange(e, setFieldValue)}
                            />
                            <ErrorMessage name="email" component="div" className="form__error" />
                          </div>
                        </div>

                        <div className="formCol">
                          <div className="form__group">
                            <label htmlFor="name" className="form__label">
                              Name
                              <sup className="form__label__asterisk">*</sup>
                            </label>
                            <Field
                              type="text"
                              id="name"
                              name="name"
                              className="form__input form__input--sm"
                              placeholder="Name"
                              disabled={isPrefillFieldsDisabled && !editMode}
                            />
                            <ErrorMessage
                              name="name"
                              component="div"
                              className="form__error"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="formRow formRow__2">
                        <div className="formCol">
                          <div className="form__group">
                            <label htmlFor="role" className="form__label">
                              Role
                              <sup className="form__label__asterisk">*</sup>
                            </label>
                            <Field
                              as="select"
                              id="role"
                              name="role"
                              className="form__select form__select--sm"
                              style={customStyles.select}
                              disabled={isRoleDisabled || ((values.role === 'Cardiologist' || values.role === 'Radiologist' || values.role === 'Admin' || values.role === 'Sonographer' || values.role === 'Practice Manager' || values.role === 'Medical Receptionist') && editMode)}
                              onChange={(e) => {
                                // Group Admin picks a clinic group in the same field, so clear it on a switch.
                                if ((e.target.value === 'Group Admin') !== (values.role === 'Group Admin')) {
                                  setFieldValue('clinicName', '');
                                  setSelectedGroupId('');
                                }
                                setFieldValue('role', e.target.value);
                              }}
                            >
                              <option value="">-Select-</option>
                              {filteredRoles?.map((role, index) => (
                                <option key={index} value={role}>
                                  {role}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage
                              name="role"
                              component="div"
                              className="form__error"
                            />
                          </div>
                        </div>

                        <div className="formCol">
                          {values.role === 'Group Admin' ? (
                          <div className="form__group">
                            <label htmlFor="clinicName" className="form__label">
                              Clinic Group
                              <sup className="form__label__asterisk">*</sup>
                            </label>
                            <Field
                              as="select"
                              id="clinicName"
                              name="clinicName"
                              className="form__select form__select--sm"
                              style={customStyles.select}
                              disabled={editMode}
                              onChange={(e) => {
                                setFieldValue('clinicName', e.target.value);
                                setSelectedGroupId(e.target.options[e.target.selectedIndex].getAttribute('data-id') || '');
                              }}
                            >
                              <option value="">-Select-</option>
                              {(editMode ? [{ _id: '', name: values.clinicName }] : clinicGroups).map(group => (
                                <option key={group._id || group.name} value={group.name} data-id={group._id}>
                                  {group.name}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage
                              name="clinicName"
                              component="div"
                              className="form__error"
                            />
                          </div>
                          ) : (
                          <div className="form__group">
                            <label htmlFor="clinicName" className="form__label">
                              Clinic Name
                              <sup className="form__label__asterisk">*</sup>
                            </label>
                            <Field
                              as="select"
                              id="clinicName"
                              name="clinicName"
                              className="form__select form__select--sm"
                              style={customStyles.select}
                              disabled={Role === 'Sonographer'}
                              onChange={(e) => {
                                setFieldValue('clinicName', e.target.value);
                                const selectedIndex = e.target.selectedIndex;
                                const selectedClinicId = e.target.options[selectedIndex].getAttribute('data-id');
                                if (selectedClinicId) {
                                  setSelectedHospitalId(selectedClinicId);
                                }
                              }}
                            >
                              <option value="">-Select-</option>
                              {clinicNames?.map((clinic, index) => (
                                <option key={index} value={clinic.name} data-id={clinic._id}>
                                  {clinic.name}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage
                              name="clinicName"
                              component="div"
                              className="form__error"
                            />
                          </div>
                          )}
                        </div>
                      </div>

                      <div className="formRow formRow__2">
                        <div className="formCol">
                          <div className="form__group">
                            <label htmlFor="contactNumber" className="form__label">
                              Contact Number
                              <sup className="form__label__asterisk">*</sup>
                            </label>
                            <div className="form__groupField form__groupField--phonenumber">
                             <Field name="countryCode">
                                {({ field, form }) => (
                                  <select
                                    {...field}
                                    id="countryCode"
                                    className="form__select form__select--sm"
                                    style={customStyles.contactSelect}
                                    disabled={isPrefillFieldsDisabled && !editMode}
                                    value={form.values.countryCode || '+61'}
                                    onChange={(e) => form.setFieldValue('countryCode', e.target.value)}
                                  >
                                    {sortedUniqueCountryCodes.map(({ callingCode }) => (
                                      <option key={callingCode} value={`+${callingCode}`}>
                                        +{callingCode}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </Field>
                              <Field
                                type="tel"
                                id="contactNumber"
                                name="contactNumber"
                                className="form__input form__input--sm"
                                placeholder="Contact Number"
                                maxLength={10}
                                disabled={isPrefillFieldsDisabled && !editMode}
                                onInput={(e) => {e.target.value = e.target.value.replace(/\D/g, '')}}
                              />
                            </div>
                            <ErrorMessage
                              name="contactNumber"
                              component="div"
                              className="form__error"
                            />
                          </div>
                        </div>
                      </div>
                    </section>
                  </Form>
                  </>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>
      <Loader isLoading={loading || editLoading || clinicLoading || prefillUserLoading} />
      <Success
        handleCloseModal={() => setIsSuccessModalOpen(false)}
        title={displayMessage}
        isSuccessModalOpen={isSuccessModalOpen}
      />
      <Failed
        handleCloseModal={() => setIsFailedModalOpen(false)}
        title={displayMessage}
        isFailedModalOpen={isFailedModalOpen}
      />
    </section>
  );
};

export default AddUpdateUser;
